import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { icon, latLngBounds, Map, marker, tileLayer } from 'leaflet';
import { debounceTime } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../../../../@fuse/components/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogService } from '../../../../@fuse/components/confirm-dialog/confirm-dialog.service';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { QueryCoordinatesGeocodeGQL } from '../../../main/locations/services/location.service';
import { normalizeAddress } from '../../utils/normalize-Address';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Component({
  selector: 'map-address',
  templateUrl: './map-address.component.html',
  styleUrls: ['./map-address.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MapAddressComponent implements OnInit {
  @Input() addressForm: FormGroup;
  @Input() isViewMode: boolean;

  public markerClusterGroup: any;
  public fitBoundsOptions: any;
  public markerClusterData: any[] = [];
  public readonly options = {
    zoom: 5,
    minZoom: 2,
    fullscreenControl: true,
    layers: [
      tileLayer('https://mt0.google.com/vt/lyrs=m&hl=pt&x={x}&y={y}&z={z}&s=Ga7')
    ]
  };

  public readonly layersControl = {
    baseLayers: {}
  };

  constructor(
    private readonly translate: TranslateService,
    private readonly queryCoordinatesGeocodeGQL: QueryCoordinatesGeocodeGQL,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly translateService: TranslateService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {
    this.addLayer('DEFAULT', 'https://mt0.google.com/vt/lyrs=m&hl=pt&x={x}&y={y}&z={z}&s=Ga7');
    this.addLayer('SATELLITE', 'https://mt0.google.com/vt/lyrs=y&hl=pt&x={x}&y={y}&z={z}&s=Ga7');
    this.addLayer('TERRAIN', 'https://mt0.google.com/vt/lyrs=p&hl=pt&x={x}&y={y}&z={z}&s=Ga7');

    const coords = this.addressForm.get('coords').value;
    if (coords && coords.latitude && coords.longitude) {
      this.createMarker({
        lat: coords.latitude,
        lng: coords.longitude
      });
    } else {
      this.fitBoundsOptions = latLngBounds([-47.186656, -98.472544], [5.664193, -28.775281]);
    }

    this.addressForm.valueChanges
      .pipe(debounceTime(1000))
      .subscribe((value) => {
        if (value.coords.latitude && value.coords.longitude) {
          this.markerClusterData = [];
          this.createMarker({
            lat: value.coords.latitude,
            lng: value.coords.longitude
          });
        }
      });
  }


  public createMarker({ lat, lng }: { lat: number, lng: number }): void {
    if (this.markerClusterData.length) {
      return;
    }

    this.fitBoundsOptions = latLngBounds([[lat, lng]]);

    const pointer = marker([lat, lng], {
      draggable: !this.isViewMode,
      icon: icon({
        iconUrl: 'assets/images/icons/ic_location_marker_32px.svg',
        iconSize: [32, 36],
        iconAnchor: [16, 36],
        popupAnchor: [0, -36]
      })
    });

    this.markerClusterData = [pointer];

    if (this.addressForm.get('formattedAddress').value) {
      pointer.bindPopup(this.addressForm.get('formattedAddress').value);
    }


    pointer.on('dragend', async (event) => {
      const coords = event.target.getLatLng();
      await this.queryCoordinatesGeocodeGQL.fetch({ input: { latitude: coords.lat, longitude: coords.lng } })
        .toPromise()
        .then((result) => result.data && result.data['coordinatesGeocode'])
        .then(normalizeAddress)
        .then(async (address) => {
          if (!address) { return; }
          address.coords = { latitude: coords.lat, longitude: coords.lng };
          this.addressForm.get('coords').setValue(address.coords, { emitEvent: false });

          const result = await this.confirmDialogService.open({
            title: this.translateService.instant('CONFIRM_CHANGE_ADDRESS.TITLE'),
            message: this.translateService.instant('CONFIRM_CHANGE_ADDRESS.MESSAGE', {
              previousAddress: this.addressForm.get('formattedAddress').value || this.translateService.instant('CONFIRM_CHANGE_ADDRESS.NO_ADDRESS'),
              newAddress: address.formattedAddress
            }),
            okText: this.translateService.instant('CONFIRM_CHANGE_ADDRESS.YES'),
            cancelText: this.translateService.instant('CONFIRM_CHANGE_ADDRESS.NO'),
          });

          if (!result || !result.confirmed) {
            return;
          }

          address.postalCode = address.postalCode && address.postalCode.replace(/[^0-9]/g, '');

          this.addressForm.setValue(address, { emitEvent: false });
        });

    });
  }

  private addLayer(name: string, layerUrl: string): void {
    const translatedName = this.translate.instant(`LOCATIONS.DIALOG.TABS.TAB_LOCATION.MAPS.${name}`);
    this.layersControl.baseLayers[translatedName] = tileLayer(layerUrl);
  }
}
